import { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchHotel, fetchHotelAvailability, updateHotelAvailability, updateHotelConfig, fetchHotelReservations } from "@/lib/api";
import AccountSidebar from "@/components/AccountSidebar";
import { TarifExpressCard } from "@/components/TarifExpressCard";
import { EnchereVisibiliteCard } from "@/components/EnchereVisibiliteCard";
import { HotelChambresCard } from "@/components/HotelChambresCard";
import { HotelProCard } from "@/components/HotelProCard";
import { HotelStatsSection } from "@/components/HotelStatsSection";
import { DayUseOfferCard } from "@/components/DayUseOfferCard";
import { fmtPrice } from "@/lib/utils";
import { ChevronLeft, Save, RotateCcw, Calendar, Settings, CheckCircle2, Power, BedDouble } from "lucide-react";

function pad(n) { return String(n).padStart(2, "0"); }
function todayIso() { return new Date().toISOString().slice(0, 10); }
function addDays(iso, days) { const d = new Date(iso); d.setDate(d.getDate() + days); return d.toISOString().slice(0, 10); }
function formatDateLabel(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });
}

export default function AdminHotelAvailabilityPage() {
  const { id } = useParams();
  const [hotel, setHotel] = useState(null);
  const [date, setDate] = useState(todayIso());
  const [slots, setSlots] = useState([]);
  const [config, setConfig] = useState({ off_peak_start: 0, off_peak_end: 6, off_peak_discount: 30 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  // Build week of dates starting today
  const week = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(todayIso(), i)), []);

  useEffect(() => {
    fetchHotel(id).then(setHotel).catch(() => setError("Hotel introuvable"));
  }, [id]);

  useEffect(() => {
    setLoading(true);
    fetchHotelAvailability(id, date)
      .then((d) => {
        setSlots(d.slots);
        if (d.config) setConfig({ ...config, ...d.config });
      })
      .catch(() => setError("Erreur de chargement"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, date]);

  const isOffPeakHour = (h) => {
    const s = config.off_peak_start, e = config.off_peak_end;
    return s < e ? h >= s && h < e : h >= s || h < e;
  };

  const updateSlot = (idx, key, val) => {
    setSlots((arr) => arr.map((s, i) => i === idx ? { ...s, [key]: val } : s));
  };

  const stats = useMemo(() => {
    const active = slots.filter((s) => s.is_active);
    const totalRooms = active.reduce((sum, s) => sum + (s.available_rooms || 0), 0);
    const avgRegular = active.length ? Math.round(active.reduce((s, x) => s + x.regular_price, 0) / active.length) : 0;
    return {
      activeHours: active.length,
      totalRooms,
      avgRegular,
    };
  }, [slots]);

  const save = async () => {
    setSaving(true);
    try {
      await updateHotelAvailability(id, date, slots);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur a l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await updateHotelConfig(id, config);
      // Re-fetch to recompute defaults if no record exists
      const d = await fetchHotelAvailability(id, date);
      setSlots(d.slots);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur a l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    if (!window.confirm("Reinitialiser les creneaux du jour aux valeurs par defaut de l'hotel ?")) return;
    // Just re-fetch (server returns defaults when no record exists, but here a record exists so manual reset):
    const base = hotel?.price_public || hotel?.price || 35000;
    const next = Array.from({ length: 24 }, (_, h) => ({
      hour: h,
      available_rooms: hotel?.rooms_available || 3,
      regular_price: base,
      off_peak_price: isOffPeakHour(h) ? Math.max(5000, Math.floor(base * (1 - config.off_peak_discount / 100))) : base,
      is_active: true,
    }));
    setSlots(next);
  };

  if (error && !hotel) {
    return <main className="max-w-3xl mx-auto px-6 py-20 text-center" data-testid="admin-hotel-error"><div className="text-lg font-bold">{error}</div></main>;
  }

  return (
    <main className="max-w-7xl mx-auto px-6 py-10" data-testid="admin-hotel-availability-page">
      <Link to="/admin/hotels" className="inline-flex items-center gap-1 text-orange-600 font-semibold mb-4" data-testid="admin-back">
        <ChevronLeft size={16} /> Retour aux hotels
      </Link>
      <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight">
        Disponibilites <span className="text-orange-600">{hotel?.name || ""}</span>
      </h1>
      <p className="text-[var(--ink-soft)] mt-1">Gerez les chambres disponibles et les tarifs heure par heure.</p>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        <AccountSidebar />

        <section className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatTile label="Creneaux actifs" value={`${stats.activeHours} / 24`} testid="creneaux-actifs" />
            <StatTile label="Chambres totales" value={stats.totalRooms} testid="chambres-totales" />
            <StatTile label="Prix moyen" value={fmtPrice(stats.avgRegular)} testid="prix-moyen" />
            <StatTile label="Remise off-peak" value={`${config.off_peak_discount}%`} testid="remise" />
          </div>

          {/* Pro Hotel + Tarif Express */}
          {hotel && <HotelProCard hotel={hotel} onChanged={() => fetchHotel(id).then(setHotel).catch(() => {})} />}
          {hotel && <TarifExpressCard hotel={hotel} />}
          {hotel && <EnchereVisibiliteCard hotel={hotel} />}
          {hotel && <HotelChambresCard hotel={hotel} />}
          {hotel && <DayUseOfferCard hotel={hotel} onChanged={() => fetchHotel(id).then(setHotel).catch(() => {})} />}
          <HotelStatsSection hotelId={id} />

          {/* Date selector */}
          <div className="glass-card p-5">
            <div className="flex items-center gap-2 text-sm font-bold mb-3"><Calendar size={16} className="text-orange-600" /> Choisir une date</div>
            <div className="flex gap-2 flex-wrap" data-testid="date-selector">
              {week.map((d) => (
                <button
                  key={d}
                  onClick={() => setDate(d)}
                  className={`chip ${d === date ? "active" : ""}`}
                  data-testid={`date-${d}`}
                >
                  {formatDateLabel(d)}
                </button>
              ))}
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="!w-auto !py-2"
                data-testid="date-input"
              />
            </div>
          </div>

          {/* Tarification config */}
          <div className="glass-card p-5">
            <div className="flex items-center gap-2 text-sm font-bold mb-4"><Settings size={16} className="text-orange-600" /> Tarification heures creuses</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label>Debut</label>
                <input type="number" min={0} max={23} value={config.off_peak_start} onChange={(e) => setConfig({ ...config, off_peak_start: Number(e.target.value) })} data-testid="config-start" />
              </div>
              <div>
                <label>Fin (exclu)</label>
                <input type="number" min={0} max={24} value={config.off_peak_end} onChange={(e) => setConfig({ ...config, off_peak_end: Number(e.target.value) })} data-testid="config-end" />
              </div>
              <div>
                <label>Remise (%)</label>
                <input type="number" min={0} max={90} value={config.off_peak_discount} onChange={(e) => setConfig({ ...config, off_peak_discount: Number(e.target.value) })} data-testid="config-discount" />
              </div>
              <div className="flex items-end">
                <button onClick={saveConfig} disabled={saving} className="btn-primary w-full" data-testid="config-save">
                  Enregistrer la config
                </button>
              </div>
            </div>
            <div className="text-xs text-[var(--ink-soft)] mt-2">
              Heures off-peak actuelles : {pad(config.off_peak_start)}:00 - {pad(config.off_peak_end)}:00. La remise est appliquee automatiquement aux nouveaux creneaux.
            </div>
          </div>

          {/* 24h grid */}
          <div className="glass-card p-5">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <div className="font-bold">Grille 24 h - {formatDateLabel(date)}</div>
              <div className="flex gap-2">
                <button onClick={resetToDefaults} className="btn-secondary !text-xs inline-flex items-center gap-1" data-testid="reset-defaults">
                  <RotateCcw size={12} /> Reinitialiser
                </button>
                <button onClick={save} disabled={saving || loading} className="btn-primary inline-flex items-center gap-1" data-testid="save-availability">
                  <Save size={14} /> {saving ? "Enregistrement..." : "Enregistrer"}
                </button>
              </div>
            </div>

            {saved && (
              <div className="mb-4 px-4 py-2 rounded-xl bg-green-50 text-green-700 text-sm font-semibold inline-flex items-center gap-2" data-testid="save-toast">
                <CheckCircle2 size={14} /> Modifications enregistrees
              </div>
            )}
            {error && (
              <div className="mb-4 px-4 py-2 rounded-xl bg-red-50 text-red-600 text-sm font-semibold">{error}</div>
            )}

            {loading ? (
              <div className="text-center py-10">Chargement...</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="hour-grid">
                {slots.map((s, i) => {
                  const isOff = isOffPeakHour(s.hour);
                  return (
                    <div
                      key={s.hour}
                      className={`rounded-2xl border p-4 transition-colors ${
                        s.is_active ? "border-[var(--line)] bg-white" : "border-dashed border-gray-300 bg-gray-50/50 opacity-70"
                      }`}
                      data-testid={`slot-${s.hour}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="font-bold text-orange-600">{pad(s.hour)}:00</div>
                        <div className="flex items-center gap-2">
                          {isOff && <span className="text-[10px] uppercase tracking-widest text-orange-600 font-bold">Off-peak</span>}
                          <button
                            onClick={() => updateSlot(i, "is_active", !s.is_active)}
                            className={`w-9 h-5 rounded-full relative transition-colors ${s.is_active ? "bg-green-500" : "bg-gray-300"}`}
                            data-testid={`slot-toggle-${s.hour}`}
                            aria-label="Activer/Desactiver"
                          >
                            <span className={`absolute top-0.5 ${s.is_active ? "left-[18px]" : "left-0.5"} w-4 h-4 bg-white rounded-full transition-all`} />
                          </button>
                        </div>
                      </div>
                      <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <label className="!mb-1">Chambres</label>
                          <input
                            type="number"
                            min={0}
                            value={s.available_rooms}
                            onChange={(e) => updateSlot(i, "available_rooms", Math.max(0, Number(e.target.value)))}
                            className="!px-2 !py-1.5 !text-sm"
                            data-testid={`slot-rooms-${s.hour}`}
                          />
                        </div>
                        <div>
                          <label className="!mb-1">Prix normal</label>
                          <input
                            type="number"
                            min={0}
                            step={1000}
                            value={s.regular_price}
                            onChange={(e) => updateSlot(i, "regular_price", Number(e.target.value))}
                            className="!px-2 !py-1.5 !text-sm"
                            data-testid={`slot-regular-${s.hour}`}
                          />
                        </div>
                        <div>
                          <label className="!mb-1">Off-peak</label>
                          <input
                            type="number"
                            min={0}
                            step={1000}
                            value={s.off_peak_price}
                            onChange={(e) => updateSlot(i, "off_peak_price", Number(e.target.value))}
                            className="!px-2 !py-1.5 !text-sm"
                            data-testid={`slot-off-${s.hour}`}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </div>

      <ReservationsSection hotelId={id} />
    </main>
  );
}

function ReservationsSection({ hotelId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    fetchHotelReservations(hotelId).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  };
  useEffect(load, [hotelId]); // eslint-disable-line react-hooks/exhaustive-deps

  const reservations = data?.reservations || [];
  return (
    <section className="glass-card p-5 mt-6" data-testid="hotel-reservations-section">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-bold text-lg inline-flex items-center gap-2">
          <BedDouble size={18} className="text-orange-500" /> Réservations reçues
          <span className="text-xs font-bold bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">{reservations.length}</span>
        </h2>
        <button type="button" onClick={load} className="text-xs font-semibold text-orange-600 hover:underline inline-flex items-center gap-1" data-testid="reservations-refresh-btn">
          <RotateCcw size={12} /> Actualiser
        </button>
      </div>
      {loading ? (
        <div className="text-sm text-[var(--ink-soft)] py-6 text-center">Chargement...</div>
      ) : reservations.length === 0 ? (
        <div className="text-sm text-[var(--ink-soft)] py-6 text-center" data-testid="reservations-empty">
          Aucune réservation pour le moment. Elles apparaîtront ici dès qu'un client réservera.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-[var(--ink-soft)] border-b border-orange-100">
                <th className="py-2 pr-3">Ref</th>
                <th className="py-2 pr-3">Client</th>
                <th className="py-2 pr-3">Téléphone</th>
                <th className="py-2 pr-3">Date</th>
                <th className="py-2 pr-3">Heure</th>
                <th className="py-2 pr-3">Ch.</th>
                <th className="py-2 pr-3">Type</th>
                <th className="py-2">Total</th>
              </tr>
            </thead>
            <tbody>
              {reservations.map((r) => (
                <tr key={r.id} className="border-b border-orange-50 hover:bg-orange-50/40" data-testid={`reservation-row-${r.ref}`}>
                  <td className="py-2.5 pr-3 font-mono text-xs font-bold text-orange-600">{r.ref}</td>
                  <td className="py-2.5 pr-3 font-semibold">{r.name}</td>
                  <td className="py-2.5 pr-3">
                    <a href={`tel:${r.phone}`} className="font-mono text-xs hover:underline">{r.phone}</a>
                  </td>
                  <td className="py-2.5 pr-3">{r.date}</td>
                  <td className="py-2.5 pr-3">{pad(r.hour)}:00{r.off_peak ? " 🌙" : ""}</td>
                  <td className="py-2.5 pr-3">{r.rooms}</td>
                  <td className="py-2.5 pr-3 text-xs" data-testid={`reservation-room-type-${r.ref}`}>{r.room_type ? <span className="px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 font-semibold">{r.room_type}</span> : <span className="text-neutral-400">—</span>}</td>
                  <td className="py-2.5 font-bold">{fmtPrice(r.total_price)}
                    <div className={`text-[10px] font-bold uppercase tracking-wider ${r.deposit_paid ? "text-emerald-700" : r.status === "en_attente_acompte" ? "text-amber-600" : r.status === "annulee" || r.status === "expiree" ? "text-red-600" : "text-[var(--ink-soft)]"}`} data-testid={`reservation-deposit-${r.ref}`}>
                      {r.deposit_paid ? `Acompte payé ✓ ${fmtPrice(r.deposit_amount)}` : r.status === "en_attente_acompte" ? "Acompte en attente" : r.status === "annulee" ? `Annulée${r.penalty_amount ? ` · pénalité ${fmtPrice(r.penalty_amount)}` : ""}` : r.status === "expiree" ? "Expirée (non payée)" : "Confirmée"}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function StatTile({ label, value, testid }) {
  return (
    <div className="glass-card p-4" data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-[var(--ink-soft)]">{label}</div>
      <div className="mt-1 text-2xl font-extrabold text-orange-600">{value}</div>
    </div>
  );
}
